import { useCallback } from 'react';
import { Product } from 'types';

/** Counter for generating unique client IDs */
let clientIdCounter = 0;

/** Generate a unique client ID for a line item */
export const generateClientId = (): string => {
  clientIdCounter += 1;
  return `line-${Date.now()}-${clientIdCounter}`;
};

export interface FormLineItem {
  /** Database ID (only present for existing line items) */
  id?: number;
  /** Unique client-side ID for React key (stable across re-renders) */
  clientId: string;
  product: Product | null;
  quantity: number;
  _destroy?: boolean;
  /** Stored line price from API (for existing lines, to match view page) */
  storedPrice?: string;
  /** Stored line tax from API (for existing lines, to match view page) */
  storedTax?: string;
}

/** Create a new empty line item with a unique client ID */
export const createEmptyLineItem = (): FormLineItem => ({
  clientId: generateClientId(),
  product: null,
  quantity: 1,
});

/**
 * Calculate line total
 * For existing lines with stored values, use those to match the view page.
 * For new lines, calculate from product prices.
 */
export const calculateLineTotal = (item: FormLineItem): number => {
  if (!item.product) return 0;
  
  // Use stored price if available (for existing lines - matches view page)
  if (item.storedPrice !== undefined) {
    return parseFloat(item.storedPrice);
  }
  
  // Calculate from product for new lines
  return parseFloat(item.product.unit_price) * item.quantity;
};

/**
 * Calculate line tax
 * For existing lines with stored values, use those to match the view page.
 * For new lines, calculate from product prices.
 */
export const calculateLineTax = (item: FormLineItem): number => {
  if (!item.product) return 0;
  
  // Use stored tax if available (for existing lines - matches view page)
  if (item.storedTax !== undefined) {
    return parseFloat(item.storedTax);
  }
  
  // Calculate from product for new lines
  return parseFloat(item.product.unit_tax) * item.quantity;
};

export const calculateTotals = (lineItems: FormLineItem[]): { subtotal: number; tax: number; total: number } => {
  const activeLines = lineItems.filter(item => item.product);
  const total = activeLines.reduce((sum, item) => sum + calculateLineTotal(item), 0);
  const tax = activeLines.reduce((sum, item) => sum + calculateLineTax(item), 0);
  const subtotal = total - tax;
  return { subtotal, tax, total };
};

interface UseLineItemsOptions {
  onChange: (lineItems: FormLineItem[]) => void;
  onProductSelected?: () => void;
}

interface UseLineItemsResult {
  addLine: () => void;
  updateLineProduct: (index: number, product: Product | null) => void;
  updateLineQuantity: (index: number, quantity: number) => void;
  removeLine: (index: number) => void;
}

export function useLineItems(
  lineItems: FormLineItem[],
  options: UseLineItemsOptions
): UseLineItemsResult {
  const { onChange, onProductSelected } = options;

  const addLine = useCallback(() => {
    onChange([...lineItems, createEmptyLineItem()]);
  }, [lineItems, onChange]);

  const updateLineProduct = useCallback((index: number, product: Product | null) => {
    const updated = lineItems.map((item, i) =>
      i === index ? { 
        ...item, 
        product,
        // Clear stored values when product changes - prices need to be recalculated
        storedPrice: undefined,
        storedTax: undefined,
      } : item
    );
    onChange(updated);
    if (product && onProductSelected) {
      onProductSelected();
    }
  }, [lineItems, onChange, onProductSelected]);

  const updateLineQuantity = useCallback((index: number, quantity: number) => {
    const updated = lineItems.map((item, i) =>
      i === index ? { 
        ...item, 
        quantity: Math.max(1, quantity),
        // Clear stored values when quantity changes - totals need to be recalculated
        storedPrice: undefined,
        storedTax: undefined,
      } : item
    );
    onChange(updated);
  }, [lineItems, onChange]);

  const removeLine = useCallback((index: number) => {
    const updated = lineItems.filter((_, i) => i !== index);
    onChange(updated);
  }, [lineItems, onChange]);

  return { addLine, updateLineProduct, updateLineQuantity, removeLine };
}

export default useLineItems;
